import type { paths } from "@whilecat/generated/openapi/user-course.ts";
import { isNotAuthenticated } from "@whilecat/services/authentication-service.ts";
import { getCourseLevels } from "@whilecat/services/course-service.ts";
import { LevelInfo } from "@whilecat/services/dto/level-info.ts";
import { SHELL_COURSE_UUID } from "../content/shell/shell/shell-constants.js";
import { SQL_BASICS_COURSE_UUID } from "../content/sql/basics/constants.ts";
import { getClient } from "./client-utils.ts";
import { HttpStatus } from "./response-codes.ts";

const client = getClient<paths>();

export async function getUserCourseLevels(courseUuid: string) {
    if (isNotAuthenticated()) {
        return await getCourseLevels(courseUuid);
    }

    const mock = await import("./course-levels-mock.ts");
    const mocked = mock.mockLevels.get(courseUuid);
    if (mocked) {
        return mocked;
    }

    try {
        const { data, response } = await client
            .withRetries(2)
            .GET("/user/course/{courseUuid}", { params: { path: { courseUuid } } });

        if (!data) {
            throw new Error(response.statusText);
        }
        const levels = data.userCourseLevels.map(
            course =>
                new LevelInfo(
                    course.courseLevel.id,
                    course.courseLevel.name,
                    course.courseLevel.url,
                    course.courseLevel.sourcePath,
                    course.completed,
                ),
        );
        return {
            ok: true,
            enrolled: data.id !== null && data.id !== 0,
            levels,
        };
    } catch (e) {
        console.error(e);
        return await getCourseLevels(courseUuid);
    }
}

export async function completeCourseLevel(courseUuid: string, levelUuid: string) {
    await client.withRetries(3).POST("/user/course/{courseUuid}/level/{levelUuid}/complete", {
        params: { path: { courseUuid, levelUuid } },
    });
}

export async function getUserCourseLevelInfo(courseUuid: string, levelUuid: string) {
    const { data, response } = await client.withRetries(2).GET("/user/course/{courseUuid}/level/{levelUuid}", {
        params: { path: { courseUuid, levelUuid } },
    });

    return { data, response };
}

export async function isCourseLevelCompleted(courseUuid: string, levelUuid: string) {
    const { response, data } = await getUserCourseLevelInfo(courseUuid, levelUuid);

    return response.status === HttpStatus.OK && data!.completed;
}

export async function rateCourseLevel(courseUuid: string, levelUuid: string, score: number, comment: string) {
    return await client.POST("/user/course/{courseUuid}/level/{levelUuid}/rating", {
        params: { path: { courseUuid, levelUuid } },
        body: { score, comment },
    });
}

export async function enrollToCourse(courseUuid: string) {
    return await client.POST("/user/course/{courseUuid}", { params: { path: { courseUuid } } });
}

export async function leaveCourse(courseUuid: string) {
    return await client.DELETE("/user/course/{courseUuid}", { params: { path: { courseUuid } } });
}

export async function checkIfLevelAccessible(courseUuid: string, levelUuid: string) {
    if (courseUuid === SHELL_COURSE_UUID || courseUuid === SQL_BASICS_COURSE_UUID) {
        return true;
    }

    const { response } = await client.withRetries(2).GET("/user/course/{courseUuid}/level/{levelUuid}/accessible", {
        params: { path: { courseUuid, levelUuid } },
    });
    return response.ok;
}

export async function completeTask(courseUuid: string, levelUuid: string, taskUuid: string, repeatable: boolean) {
    return await client.POST("/user/course/{courseUuid}/level/{levelUuid}/task/{taskUuid}", {
        params: { path: { courseUuid, levelUuid, taskUuid } },
        body: { repeatable },
    });
}

export async function getCompletedTasks(courseUuid: string, levelUuid: string) {
    const { response, data } = await client.withRetries(2).GET("/user/course/{courseUuid}/level/{levelUuid}/task", {
        params: { path: { courseUuid, levelUuid } },
    });

    if (!data) {
        throw new Error(response.statusText);
    }

    return data!;
}
